function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { useLazyQuery as useApolloLazyQuery } from "@apollo/client";
import { useMemoizedValue } from "@sail/react";
import { useCallback, useMemo } from "react";
import useGetApolloContext from "../../internal/apollo/useGetApolloContext";
import { DEFAULT_IGNORE_TRACKING_FIELDS, DEFAULT_TRACK_ACCESSES_SAMPLE_RATE, useTrackGraphQlFieldAccesses } from "../../internal/deadwood/deadwood";
/**
 * @see https://sail.stripe.me/data/api/useLazyQuery
 */ export default function useLazyQuery(/**
   * The GraphQL query that is going to be executed. This must be created with the
   * [`gql`](/apis/data/gql) or
   * [`graphql`](/apis/data/graphql) template literal.
   */ query, /**
   * The default options to pass to the query. This argument is optional.
   */ options = {}) {
    const baseContext = useGetApolloContext(options);
    const queryOptions = useMemo(()=>_object_spread_props(_object_spread({}, options), {
            context: baseContext
        }), [
        baseContext,
        options
    ]);
    const _useMemo = useMemo(()=>{
        const { variables } = queryOptions, rest = _object_without_properties(queryOptions, [
            "variables"
        ]);
        return {
            hookVariables: variables,
            hookOptionsWithoutVariables: rest
        };
    }, [
        queryOptions
    ]), { hookVariables, hookOptionsWithoutVariables } = _useMemo;
    const memoizedHookVariables = useMemoizedValue(hookVariables);
    const [callback, result] = useApolloLazyQuery(query, hookOptionsWithoutVariables);
    const callbackWrapped = useCallback(/*#__PURE__*/ _async_to_generator(function*(...restArgs) {
        const cbOptions = restArgs[0];
        const mergedVariables = _object_spread({}, memoizedHookVariables, cbOptions === null || cbOptions === void 0 ? void 0 : cbOptions.variables);
        const mergedOptions = _object_spread_props(_object_spread({}, cbOptions), {
            variables: mergedVariables
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _ref = yield callback(mergedOptions), { refetch } = _ref, result = _object_without_properties(_ref, [
            "refetch"
        ]);
        return result;
    }), [
        callback,
        memoizedHookVariables
    ]);
    var _options_trackAccesses;
    return [
        callbackWrapped,
        useTrackGraphQlFieldAccesses(result, {
            query,
            sampleRate: (_options_trackAccesses = options.trackAccesses) !== null && _options_trackAccesses !== void 0 ? _options_trackAccesses : DEFAULT_TRACK_ACCESSES_SAMPLE_RATE,
            ignoreFields: DEFAULT_IGNORE_TRACKING_FIELDS
        })
    ];
}

//# sourceMappingURL=useLazyQuery.js.map